import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  makeStyles,
} from "@material-ui/core";
import CardGiftcardIcon from "@material-ui/icons/CardGiftcard";
import { connect } from "react-redux";
import {
  async_fetch_events,
  sendComplementaryTicket,
} from "src/actions/event.action";
import EventListResults from "src/components/events/EventListResults";

const TOKENS = {
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  accent: "#222222",
  accentDark: "#000000",
  border: "#EEF0F5",
};

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: "#F5F6FA",
    minHeight: "100%",
    paddingTop: 24,
    paddingBottom: 48,
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 12,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: TOKENS.textPrimary,
  },
  pageSubtitle: {
    fontSize: 14,
    color: TOKENS.textSecondary,
    marginTop: 2,
  },
  headerActions: {
    display: "flex",
    gap: 10,
  },
  primaryButton: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    borderRadius: 8,
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 1px 2px rgba(34, 34, 34, 0.3)",
    "&:hover": { backgroundColor: TOKENS.accentDark },
  },
  outlinedButton: {
    color: TOKENS.accent,
    borderColor: TOKENS.border,
    borderRadius: 8,
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    "&:hover": { borderColor: "#A5B4FC", backgroundColor: "#EEF2FF" },
  },
  dialogPaper: {
    borderRadius: 16,
    border: `1px solid ${TOKENS.border}`,
    boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
  },
  dialogTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: TOKENS.textPrimary,
    padding: "20px 24px 8px 24px",
  },
  dialogContent: {
    padding: "12px 24px 24px 24px",
  },
  dialogActions: {
    padding: "12px 24px 20px 24px",
    gap: 8,
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 14,
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#A5B4FC" },
      "&.Mui-focused fieldset": { borderColor: TOKENS.accent },
    },
    "& .MuiInputLabel-root": { fontSize: 14, color: TOKENS.textSecondary },
  },
  cancelBtn: {
    color: TOKENS.textSecondary,
    borderRadius: 8,
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 500,
    textTransform: "none",
  },
  submitBtn: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    borderRadius: 8,
    padding: "8px 24px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    "&:hover": { backgroundColor: TOKENS.accentDark },
  },
}));

// Name used for a free pass when the event's own ticket types don't fit.
const FREE_TICKET = "Complimentary";

const emptyForm = () => ({
  eventId: "",
  kind: "free", // "free" pass, or paid the host "offline"
  ticket: FREE_TICKET,
  number_of_tickets: 1,
  amount_paid: "",
  name: "",
  email: "",
  phone_number: "",
});

function EventsList({ async_fetch_events, events, sendComplementaryTicket }) {
  const classes = useStyles();

  useEffect(() => {
    async_fetch_events();
  }, [async_fetch_events]);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);

  const event = events.find((e) => e.id === form.eventId);
  const ticketTypes = event?.tickets || [];
  const isOffline = form.kind === "offline";

  const openDialog = () => {
    setForm(emptyForm());
    setOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Changing event or kind resets the ticket type: an offline sale must be one
  // of the event's ticket types, a free pass defaults to "Complimentary".
  const pick = (changes) =>
    setForm((prev) => {
      const next = { ...prev, ...changes };
      const types = events.find((e) => e.id === next.eventId)?.tickets || [];
      next.ticket =
        next.kind === "offline" ? types[0]?.ticket_name || "" : FREE_TICKET;
      return next;
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const sent = await sendComplementaryTicket(form.eventId, {
      kind: form.kind,
      ticket: form.ticket,
      number_of_tickets: Number(form.number_of_tickets) || 1,
      name: form.name,
      email: form.email,
      phone_number: form.phone_number,
      ...(isOffline && {
        amount_paid: Number(form.amount_paid),
        currency: event?.currency,
      }),
    });
    setSending(false);
    if (sent) setOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>Tubayo Host | Events</title>
      </Helmet>
      <Box className={classes.root}>
        <Container maxWidth={false}>
          <Box className={classes.pageHeader}>
            <Box>
              <Typography className={classes.pageTitle}>Events</Typography>
              <Typography className={classes.pageSubtitle}>
                Manage your listed events
              </Typography>
            </Box>
            <Box className={classes.headerActions}>
              <Button
                variant="outlined"
                className={classes.outlinedButton}
                startIcon={<CardGiftcardIcon style={{ fontSize: 18 }} />}
                onClick={openDialog}
              >
                Complimentary Ticket
              </Button>
            </Box>
          </Box>

          <EventListResults events={events} />

          {/* Complimentary / offline ticket dialog */}
          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              className: classes.dialogPaper,
              component: "form",
              onSubmit: handleSubmit,
            }}
          >
            <DialogTitle disableTypography className={classes.dialogTitle}>
              Send a Ticket
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    required
                    className={classes.textField}
                  >
                    <InputLabel>Event</InputLabel>
                    <Select
                      value={form.eventId}
                      onChange={(e) => pick({ eventId: e.target.value })}
                      label="Event"
                      style={{ borderRadius: 8 }}
                    >
                      {events.map((ev) => (
                        <MenuItem key={ev.id} value={ev.id}>
                          {ev.event_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <RadioGroup
                    row
                    value={form.kind}
                    onChange={(e) => pick({ kind: e.target.value })}
                  >
                    <FormControlLabel
                      value="free"
                      control={<Radio color="default" />}
                      label="Free pass"
                    />
                    <FormControlLabel
                      value="offline"
                      control={<Radio color="default" />}
                      label="Paid offline (cash / mobile money)"
                    />
                  </RadioGroup>
                </Grid>
                <Grid item xs={12} md={8}>
                  {isOffline ? (
                    <FormControl
                      variant="outlined"
                      fullWidth
                      required
                      className={classes.textField}
                    >
                      <InputLabel>Ticket type</InputLabel>
                      <Select
                        name="ticket"
                        value={form.ticket}
                        onChange={handleChange}
                        label="Ticket type"
                        style={{ borderRadius: 8 }}
                      >
                        {ticketTypes.map((t) => (
                          <MenuItem key={t.ticket_name} value={t.ticket_name}>
                            {t.ticket_name} · {event.currency || ""}{" "}
                            {Number(t.ticket_price).toLocaleString()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      name="ticket"
                      label="Ticket name"
                      value={form.ticket}
                      onChange={handleChange}
                      helperText="Shown on the ticket, e.g. Complimentary, VIP guest"
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    className={classes.textField}
                    required
                    variant="outlined"
                    fullWidth
                    type="number"
                    inputProps={{ min: 1, max: 50 }}
                    name="number_of_tickets"
                    label="Tickets"
                    value={form.number_of_tickets}
                    onChange={handleChange}
                  />
                </Grid>
                {isOffline && (
                  <Grid item xs={12}>
                    <TextField
                      className={classes.textField}
                      required
                      variant="outlined"
                      fullWidth
                      type="number"
                      inputProps={{ min: 1 }}
                      name="amount_paid"
                      label={`Amount paid${event?.currency ? ` (${event.currency})` : ""}`}
                      value={form.amount_paid}
                      onChange={handleChange}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    className={classes.textField}
                    required
                    variant="outlined"
                    fullWidth
                    name="name"
                    label="Full Name"
                    value={form.name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    className={classes.textField}
                    variant="outlined"
                    fullWidth
                    name="phone_number"
                    label="Phone Number"
                    value={form.phone_number}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    className={classes.textField}
                    required
                    variant="outlined"
                    fullWidth
                    name="email"
                    label="Email Address (the ticket is sent here)"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button
                className={classes.cancelBtn}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className={classes.submitBtn}
                type="submit"
                disabled={sending || !form.eventId}
              >
                {sending ? "Sending..." : "Send Ticket"}
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </>
  );
}

const mapStateToProps = (state) => ({
  events: state.eventReducer.events,
});

export default connect(mapStateToProps, {
  async_fetch_events,
  sendComplementaryTicket,
})(EventsList);
